import { MediaType } from "@colibrio/colibrio-reader-framework/colibrio-core-io-base";
import type {IResourceMetadata, IRandomAccessDataSource, IResourceProvider} from "@colibrio/colibrio-reader-framework/colibrio-core-io-base";
import { ZipResourceProvider } from "@colibrio/colibrio-reader-framework/colibrio-core-io-resourceprovider-zip";
import { PublicationType as ColibrioPublicationType  } from "@colibrio/colibrio-reader-framework/colibrio-core-publication-base";
import type {IPublication} from "@colibrio/colibrio-reader-framework/colibrio-core-publication-base";
import { EpubOcfResourceProvider } from "@colibrio/colibrio-reader-framework/colibrio-core-publication-epub";
import { PdfPublication } from "@colibrio/colibrio-reader-framework/colibrio-core-publication-pdf";
import { WpPublication } from "@colibrio/colibrio-reader-framework/colibrio-core-publication-wp";
import type { IReaderPublication, IReaderPublicationOptions, IReaderView, IReadingSessionOptions, IReadingSystemEngine } from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import { ReadingSystemEngine } from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-engine";
import { EpubFormatAdapter  } from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-formatadapter-epub";
import type {IEpubReaderPublicationOptions} from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-formatadapter-epub";
import { PdfFormatAdapter } from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-formatadapter-pdf";
import { WpAudiobookFormatAdapter } from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-formatadapter-wp-audiobook";
import type { ICirrusReaderUserProfileData, ICirrusReaderViewOptions } from "../shared/CirrusReaderModel";
import { CirrusReaderDefaultColibrioEpubReaderPublicationOptions, CirrusReaderDefaultColibrioReaderViewOptions } from "../shared/CirrusReaderDefaultOptions";
import { useCirrusHttpDataSource } from "~~/layers/reader/app/utils/CirrusHttpDataSource";
import { PublicationType } from "../../../../app/configuration/types";

export default defineNuxtPlugin({
    name: 'cirrus-reader-reading-system',
    enforce: 'pre',
    async setup (_nuxtApp) {
        const { variant } = useAppVariant()
        if (variant.value !== 'reader') return {};
        
        console.info('[CirrusPlugin] CirrusReaderReadingSystem.setup()');
        
        let _colibrioReadingSystem: IReadingSystemEngine;
        let _colibrioEpubReaderPublicationOptions: IEpubReaderPublicationOptions;
        let _cirrusReaderUserProfileData: ICirrusReaderUserProfileData;
        let _colibrioResourceProvider: IResourceProvider | undefined = undefined;

        const _isInAccessibilityMode: boolean = false;

        const initReadingSystem = (
            licenseApiKey: string,
            cirrusUserProfileData: ICirrusReaderUserProfileData,
        ) => {
            console.log("CirrusReaderReadingSystem.initReadingSystem");

            _colibrioReadingSystem = new ReadingSystemEngine({
                licenseApiKey: licenseApiKey,
                platformWorkaroundOptions: {
                    prepareSyncMediaAudioElementsOnFirstUserInteraction: true
                }
            });
    
            _cirrusReaderUserProfileData = cirrusUserProfileData;    
            _colibrioEpubReaderPublicationOptions = CirrusReaderDefaultColibrioEpubReaderPublicationOptions;
        }

        const destroy = (): void => {
            if (_colibrioReadingSystem)
                _colibrioReadingSystem.destroy();
        }
    
        const createReaderView = (_initialViewOptions: ICirrusReaderViewOptions | undefined): IReaderView => {
            console.log("CirrusReaderReadingSystem.createReaderView");
            const viewOptions = Object.assign({}, CirrusReaderDefaultColibrioReaderViewOptions, _initialViewOptions);
            const readerView = _colibrioReadingSystem.createReaderView(viewOptions);
            if (viewOptions.activeRendererTypeName !== undefined){
                readerView.setActiveRenderer(readerView.getRendererByName(viewOptions.activeRendererTypeName.toString()));
            }
            return readerView;
        }

        const loadPublicationFromUrl = async(user?: string, sessionToken?: string): Promise<IReaderPublication> => {
            console.log("CirrusReaderReadingSystem.loadPublicationFromUrl");

            return _getPublicationFromUrl(user, sessionToken).then(async (publication) => {
                let loadPublicationPromise: Promise<IReaderPublication>;
    
                const readingSessionOptions: IReadingSessionOptions = {
                    publicationToken: publication.getHashSignature(),
                    userToken: _cirrusReaderUserProfileData.token,
                };
    
                switch (publication.getType()) {
                    case ColibrioPublicationType.EPUB:
                    {
                        loadPublicationPromise = _colibrioReadingSystem.loadPublication(publication, _colibrioEpubReaderPublicationOptions, readingSessionOptions);
                        break;
                    }
                    case ColibrioPublicationType.PDF:
                    {
                        const pdfReaderPublicationOptions: IReaderPublicationOptions = {};
                        loadPublicationPromise = _colibrioReadingSystem.loadPublication(publication, pdfReaderPublicationOptions, readingSessionOptions);
                        break;
                    }
                    case ColibrioPublicationType.WP:
                    default:
                    {
                        const defaultReaderPublicationOptions: IReaderPublicationOptions = {};
                        loadPublicationPromise = _colibrioReadingSystem.loadPublication(publication, defaultReaderPublicationOptions, readingSessionOptions);
                
                    }
                }
                return loadPublicationPromise;
            });
        }


        const _getPublicationFromUrl = async (user?: string, sessionToken?: string): Promise<IPublication> => {
            console.log("CirrusReaderReadingSystem._getPublicationFromUrl");
            const cirrusConfigStore = useCirrusConfigStore();
            const resourceInfo: IResourceMetadata | undefined = await useCirrusHttpHeaderReader(user, sessionToken);
            if (!resourceInfo){
                throw createError({ status: 400, statusText: "Unable to load publication URL header", fatal: true });
            }
            const resourceSize: number | undefined = resourceInfo.size;
            const mediaType: MediaType | string | undefined = resourceInfo.mediaType;
            let publication: IPublication | null = null;

            const MAX_CHUNK = Math.round((resourceSize || 800000) / 20);
            const dataSource: IRandomAccessDataSource | undefined = useCirrusHttpDataSource(resourceSize || 0, user, sessionToken);
            if (!dataSource){
                throw new Error('Unable to get data source');
            }
            switch (mediaType) {
                case MediaType.APPLICATION_EPUB_ZIP:
                {
                    if (cirrusConfigStore.config?.public.formats?.includes(PublicationType.enum.EPUB)){
                        _colibrioReadingSystem.addFormatAdapter(new EpubFormatAdapter());
                        const zipResourceProvider = await ZipResourceProvider.createFromRandomAccessDataSource(dataSource, {
                            transferBuffers: true,
                            maxChunkSize: MAX_CHUNK,
                            chunkCacheSize: 50000,
                        });
                        const epubOcfResourceProvider = await EpubOcfResourceProvider.createFromBackingResourceProvider(zipResourceProvider, zipResourceProvider.getCentralDirectorySha1Signature());
                        publication = epubOcfResourceProvider.getDefaultPublication();
                        
                        _colibrioResourceProvider = epubOcfResourceProvider;
                    }
                    break;
                }
                case MediaType.APPLICATION_PDF:
                {
                    if (cirrusConfigStore.config?.public.formats?.includes(PublicationType.enum.PDF)){
                        _colibrioReadingSystem.addFormatAdapter(new PdfFormatAdapter());
                        publication = await PdfPublication.createFromRandomAccessDataSource(dataSource, {
                            enableDeterministicChunkRequests: true,
                            //chunkSize: MAX_CHUNK,
                        });
                    }
                    break;
                }
                case MediaType.APPLICATION_LPF_ZIP:
                {
                    if (cirrusConfigStore.config?.plan == "pro" && cirrusConfigStore.config?.public.formats?.includes(PublicationType.enum.WP)){
                        _colibrioReadingSystem.addFormatAdapter(new WpAudiobookFormatAdapter());
                        const backingZipResourceProvider = await ZipResourceProvider.createFromRandomAccessDataSource(dataSource, {
                            transferBuffers: true,
                            //maxChunkSize: MAX_CHUNK,
                        });
                        _colibrioResourceProvider = backingZipResourceProvider;
                        publication = await WpPublication.createFromManifestUrl(backingZipResourceProvider, 'publication.json', backingZipResourceProvider.getCentralDirectorySha1Signature(), () => {
                            backingZipResourceProvider.destroy();
                        });
                    }
                    break;
                }
            }
            if (!publication) {
                throw new Error('The file did not contain any supported publication type');
            }
            return publication;
        }
    
        return {
            provide: {
                cirrusReaderReadingSystem: {
                    initReadingSystem,
                    destroy,
                    createReaderView,
                    loadPublicationFromUrl,
                    get colibrioReadingSystem(): IReadingSystemEngine {
                        return _colibrioReadingSystem;
                    },
                    get colibrioReaderPublication(): IReaderPublication | undefined {
                        return _colibrioReadingSystem.getReaderPublications()[0];
                    },
                    get isInAccessibilityMode() {
                        return _isInAccessibilityMode;
                    },
                    get colibrioResourceProvider(): IResourceProvider | undefined {
                        return _colibrioResourceProvider;
                    }
                }
            }
        }
    }
});